// The orientation bug fix lives here.
//
// Recharts v3: a `<Tooltip>` with no `axisId` follows an arbitrary
// "first" axis when the chart has multiple axes (verified: v3 Tooltip
// API + 3.0 migration guide). The cartesian tile always has ≥2 axes, so
// the tooltip/cursor bound to the wrong axis whenever the resolved
// "first" axis was the value axis — the cursor rendered with the wrong
// orientation and the payload/label came out empty or wrong. Pinning
// `axisId` to the category/index axis makes activation deterministic in
// *both* orientations.
//
// The cursor must also match the series kind: a stroke-only line cursor
// on a bar chart is invisible/wrong (this is why vertical *bar* charts
// looked broken). Bars get a translucent filled band; line/area keep a
// stroke cursor. Recharts orients the cursor element itself from the
// chart `layout` once the tooltip follows the right axis.

import { Tooltip } from 'recharts';

import classes from '../CartesianPanel.module.css';
import type { LabelFormatter } from './axisLabels.tsx';
import type { ChartOrientation } from './chartOrientation.ts';

const BAR_CURSOR = { fill: 'var(--mantine-color-dark-2)', fillOpacity: 0.2 } as const;
const LINE_CURSOR = { stroke: 'var(--mantine-color-dark-2)', strokeOpacity: 0.5 } as const;

export function OrientedTooltip({
  o,
  hasBar,
  valueFormatter,
  labelFormatter,
}: {
  o: ChartOrientation;
  hasBar: boolean;
  /** Formats a tooltip value; recharts passes the series name + item so units can key off `dataKey`. */
  valueFormatter: (
    value: number | string | readonly (string | number)[] | undefined,
    name?: unknown,
    item?: { readonly dataKey?: string | number },
  ) => string;
  labelFormatter: LabelFormatter;
}) {
  return (
    <Tooltip
      allowEscapeViewBox={{ x: false, y: false }}
      axisId={o.tooltipAxisId}
      cursor={hasBar ? BAR_CURSOR : LINE_CURSOR}
      formatter={(value, name, item) => valueFormatter(value, name, item as { readonly dataKey?: string | number })}
      isAnimationActive={false}
      labelFormatter={labelFormatter}
      wrapperClassName={classes.tooltip}
    />
  );
}
